import { useEffect } from 'react'
import { useAppStore } from '@shared/store'
import { Header } from '@widgets/header'
import { Footer } from '@widgets/footer'
import { useSeo } from '@shared/lib/seo'
import { SITE } from '@shared/config'
import * as s from './LegalPage.css'

export function PrivacyPolicyPage() {
  useSeo({
    title: `Privacy Policy | ${SITE.name}`,
    description: `How ${SITE.name} uses Google Analytics, first-party analytics cookies, affiliate click measurement, and external links.`,
    path: '/privacy-policy/',
    type: 'website',
  })
  useEffect(() => { window.scrollTo(0, 0) }, [])
  const isDarkMode = useAppStore((st) => st.isDarkMode)

  return (
    <>
      <Header />
      <main className={s.page}>
        <section className={s.hero}>
          <div className={s.heroBrandRow}>
            <img src="/images/plantology_logo.png" alt="Plantology" className={s.heroLogo} style={isDarkMode ? { filter: 'invert(1)' } : undefined} />
            <span className={s.heroBrandDivider} aria-hidden="true" />
            <img src="/images/logo_black.webp" alt={SITE.name} className={s.heroSiteLogo} style={isDarkMode ? { filter: 'invert(1)' } : undefined} />
          </div>
          <p className={s.heroLabel}>Privacy</p>
          <h1 className={s.heroTitle}>Privacy Policy</h1>
          <p className={s.heroDesc}>
            {SITE.name} runs no accounts, comments, or forms. This page covers
            what Google Analytics measures here and how to opt out.
          </p>
        </section>

        <div className={s.shimmer} />

        <section className={s.section}>
          <p className={s.sectionLabel}>Measurement</p>
          <h2 className={s.sectionTitle}>What is collected</h2>
          <p className={s.sectionBody}>
            We use Google Analytics 4 (GA4) to understand which pages are visited and how
            readers use the site.
          </p>
          <div className={s.dataList}>
            <div className={s.dataRow}>
              <span className={s.dataLabel}>Analytics basics</span>
              <p className={s.dataDesc}>
                Google states that its default web implementation can collect user and
                session counts, approximate location, browser information, device
                information, and on-site activity. GA4 commonly stores a random client
                identifier in a first-party cookie named <code className={s.code}>_ga</code>.
              </p>
            </div>
            <div className={s.dataRow}>
              <span className={s.dataLabel}>Page views</span>
              <p className={s.dataDesc}>
                A page-view event is sent when a reader opens a page or moves to another
                route inside the site.
              </p>
            </div>
            <div className={s.dataRow}>
              <span className={s.dataLabel}>Affiliate clicks</span>
              <p className={s.dataDesc}>
                When a clearly marked Amazon affiliate link is selected, the site sends an{' '}
                <code className={s.code}>affiliate_click</code> event that can include the
                destination URL, destination domain, visible link text, current page URL,
                and page title. These events are used to evaluate content performance and
                affiliate-link placement.
              </p>
            </div>
            <div className={s.dataRow}>
              <span className={s.dataLabel}>What is not collected</span>
              <p className={s.dataDesc}>
                We do not intentionally send names, email addresses, or other directly
                identifying form data to Google Analytics. The site offers no reader
                accounts, comments, login forms, or contact forms.
              </p>
            </div>
          </div>
        </section>

        <div className={s.shimmer} />

        <section className={s.section}>
          <p className={s.sectionLabel}>Control</p>
          <h2 className={s.sectionTitle}>Your choices</h2>
          <div className={s.cardGrid}>
            <div className={s.card}>
              <h3 className={s.cardTitle}>Browser settings</h3>
              <p className={s.cardText}>
                Google Analytics may use first-party cookies to distinguish browsers and
                sessions. You can block or delete cookies in your browser settings.
              </p>
            </div>
            <div className={s.card}>
              <h3 className={s.cardTitle}>Analytics opt-out</h3>
              <p className={s.cardText}>
                Google provides an opt-out browser add-on that stops Analytics measurement
                across sites that use it.
              </p>
            </div>
            <div className={s.card}>
              <h3 className={s.cardTitle}>Reading is unaffected</h3>
              <p className={s.cardText}>
                Blocking analytics cookies may reduce measurement accuracy but does not
                prevent you from reading anything on the site.
              </p>
            </div>
          </div>
        </section>

        <div className={s.shimmer} />

        <section className={s.section}>
          <p className={s.sectionLabel}>Third parties</p>
          <h2 className={s.sectionTitle}>External links, retention, and changes</h2>
          <div className={s.dataList}>
            <div className={s.dataRow}>
              <span className={s.dataLabel}>Amazon links</span>
              <p className={s.dataDesc}>
                Selecting an affiliate link takes you away from {SITE.name}, and Amazon&apos;s
                own privacy terms and technologies then apply. See the Affiliate Disclosure
                and the Amazon Privacy Notice below.
              </p>
            </div>
            <div className={s.dataRow}>
              <span className={s.dataLabel}>Other external sites</span>
              <p className={s.dataDesc}>
                Articles may link to research papers, botanical institutions, government
                agencies, manufacturers, and other third-party sites. {SITE.name} does not
                control how those destinations collect or use data.
              </p>
            </div>
            <div className={s.dataRow}>
              <span className={s.dataLabel}>Retention</span>
              <p className={s.dataDesc}>
                Google processes Analytics data for {SITE.name} under Google&apos;s terms and
                the settings of the site&apos;s GA4 property. {SITE.name} uses aggregated
                reporting to assess traffic, engagement, and content performance.
              </p>
            </div>
            <div className={s.dataRow}>
              <span className={s.dataLabel}>Policy changes</span>
              <p className={s.dataDesc}>
                This policy is updated when the site&apos;s analytics, publishing features, or
                external services change.
              </p>
            </div>
          </div>
        </section>

        <div className={s.linkRow}>
          <a className={s.linkChip} href="/affiliate-disclosure/">Affiliate Disclosure</a>
          <a className={s.linkChip} href="https://support.google.com/analytics/answer/11593727" target="_blank" rel="nofollow noopener">GA4 data collection ↗</a>
          <a className={s.linkChip} href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="nofollow noopener">GA opt-out add-on ↗</a>
          <a className={s.linkChip} href="https://www.amazon.com/privacy" target="_blank" rel="nofollow noopener">Amazon Privacy Notice ↗</a>
        </div>
      </main>
      <Footer />
    </>
  )
}
